import React from 'react';
import { useSelector } from 'react-redux';
import { Content, ContextualHelp, Flex, Heading, Text } from '@adobe/react-spectrum';

import { BaseDeckPane } from './BaseDeckPane';
import { getPrettyLabel } from '../../../data/CategoryPrettyDisplayUtils';
import './bracketsPane.css';

const BRACKET_LABELS = {
    1: 'Exhibition',
    2: 'Core',
    3: 'Upgraded',
    4: 'Optimized',
    5: 'cEDH',
};

const INTENT_LEVELS = {
    1: { verdict: 'Exhibition — table-friendly', tone: 'ok' },
    2: { verdict: 'Casual — built for social play', tone: 'ok' },
    3: { verdict: 'Upgraded — focused casual', tone: 'watch' },
    4: { verdict: 'Optimized — tuned to win', tone: 'alert' },
    5: { verdict: 'Competitive — all-in', tone: 'alert' },
};

function computeCompetitiveIntent(wotcBracket, powerLevelRating) {
    if (wotcBracket === 5) return 5;
    const p = Number(powerLevelRating);
    if (Number.isNaN(p)) return 1;
    if (p >= 9) return 5;
    if (p >= 7) return 4;
    if (p >= 5) return 3;
    if (p >= 3) return 2;
    return 1;
}

const WOTC_CATEGORY_META = {
    gameChangers: {
        label: 'Game Changers',
        help: 'Cards on Wizards\' game-changer list. 0 — none; 1–3 — bracket 3; 4+ — bracket 4.',
        tiers: [
            { max: 0, bracket: 2, verdict: 'None — core safe', tone: 'ok' },
            { max: 3, bracket: 3, verdict: 'A few — upgraded tier', tone: 'watch' },
            { max: Infinity, bracket: 4, verdict: 'Dense — optimized tier', tone: 'alert' },
        ],
    },
    massLandDenial: {
        label: 'Mass Land Denial',
        help: 'Symmetrical mass land destruction (e.g. Armageddon). Any inclusion bumps the deck to bracket 4.',
        tiers: [
            { max: 0, bracket: 2, verdict: 'None — core safe', tone: 'ok' },
            { max: Infinity, bracket: 4, verdict: 'Present — optimized tier', tone: 'alert' },
        ],
    },
    extraTurns: {
        label: 'Extra Turns',
        help: 'Extra-turn spells. 0 — none; <5 — bracket 3; 5+ — bracket 4.',
        tiers: [
            { max: 0, bracket: 2, verdict: 'None — core safe', tone: 'ok' },
            { max: 4, bracket: 3, verdict: 'Splash — upgraded tier', tone: 'watch' },
            { max: Infinity, bracket: 4, verdict: 'Chainable — optimized tier', tone: 'alert' },
        ],
    },
    earlyGameInfiniteCombos: {
        label: 'Early-Game Infinite Combos',
        help: 'Two-card combos that go infinite or win with ≤7 total mana. Any presence bumps the deck to bracket 4.',
        tiers: [
            { max: 0, bracket: 2, verdict: 'None — core safe', tone: 'ok' },
            { max: Infinity, bracket: 4, verdict: 'Present — optimized tier', tone: 'alert' },
        ],
    },
};

function bracketTierFor(categoryKey, count) {
    const meta = WOTC_CATEGORY_META[categoryKey];
    if (!meta) return null;
    for (const tier of meta.tiers) {
        if (count <= tier.max) return tier;
    }
    return meta.tiers[meta.tiers.length - 1];
}

function BracketBadge({ number, label, tone, subline }) {
    return (
        <div className={`bracketBadge bracketBadge--${tone}`}>
            <span className="bracketBadgeSubline">{subline}</span>
            <span className="bracketBadgeNumber">{number ?? '—'}</span>
            <span className="bracketBadgeLabel">{label}</span>
        </div>
    );
}

function WotcCriteriaRow({ categoryKey, count, isReason }) {
    const meta = WOTC_CATEGORY_META[categoryKey];
    if (!meta) return null;
    const tier = bracketTierFor(categoryKey, count);
    const isPush = isReason || tier.bracket >= 4;

    return (
        <div
            className={`wotcCriteriaRow wotcCriteriaRow--${tier.tone}${isPush ? ' is-pushing' : ''}`}
            title={meta.help}
        >
            <span className={`wotcCriteriaDot wotcCriteriaDot--${tier.tone}`} aria-hidden="true" />
            <span className="wotcCriteriaLabel">{meta.label}</span>
            <span className="wotcCriteriaVerdict">{tier.verdict}</span>
            <span className="wotcCriteriaCount">{count}</span>
            <span className="wotcCriteriaTier">B{tier.bracket}</span>
        </div>
    );
}

function CompetitiveIntentRow({ intent }) {
    const meta = INTENT_LEVELS[intent] || INTENT_LEVELS[1];
    const isPush = intent >= 4;
    return (
        <div
            className={`wotcCriteriaRow wotcCriteriaRow--${meta.tone}${isPush ? ' is-pushing' : ''}`}
            title="Scaled from the deck's power-level rating — indicates how optimized the build plays at the table."
        >
            <span className={`wotcCriteriaDot wotcCriteriaDot--${meta.tone}`} aria-hidden="true" />
            <span className="wotcCriteriaLabel">Competitive Intent</span>
            <span className="wotcCriteriaVerdict">{meta.verdict}</span>
            <span className="wotcCriteriaCount">{intent}</span>
            <span className="wotcCriteriaTier">B{intent}</span>
        </div>
    );
}

export function BracketsPane() {
    const deck = useSelector((state) => state.deck.deck);
    const cards = useSelector((state) => state.deck.cards);

    const getCategories = () => {
        const list = {};

        Object.keys(deck?.details?.brackets?.categories || {})
            ?.filter((category) => {
                switch (category) {
                    case 'tutors':
                    case 'restock':
                        return false;
                    case 'cedhStaples':
                        return deck?.details?.brackets?.wotcBracket === 5;
                    default:
                        return true;
                }
            })
            ?.map((category) => {
                let subCategoryList = {};
                let items = [];
                let catObjectKey = category === 'earlyGameInfiniteCombos' ? 'combos' : category;
                let label = getPrettyLabel(catObjectKey);

                if (category === 'earlyGameInfiniteCombos') {
                    subCategoryList[category] = {
                        label: `${getPrettyLabel(category)}`,
                        id: category,
                        count: deck.details.brackets.categories[category].list.length,
                        list:
                            deck.details.brackets.categories[category].list.map((_key) => {
                                let source = null;
                                deck?.details?.combos?.list?.forEach((combo) => {
                                    if (combo.id === _key) {
                                        source = combo;
                                    }
                                });

                                if (source) {
                                    return {
                                        id: _key,
                                        type: 'combo',
                                        cards: (source?.cards || []).map((__card) => {
                                            if (cards.hasOwnProperty(__card)) {
                                                return cards?.[__card];
                                            }

                                            let retCard = null;
                                            Object.keys(cards || {}).forEach((cardId) => {
                                                if (cards[cardId].containerId === __card) {
                                                    if (cards[cardId].isFrontFace) {
                                                        retCard = cards[cardId];
                                                    } else {
                                                        if (!retCard) {
                                                            if (new RegExp(`_${__card}`).test(cardId)) {
                                                                retCard = cards[cardId];
                                                            } else {
                                                                if (new RegExp(`${__card}_back`).test(cardId)) {
                                                                    retCard = cards[cardId];
                                                                }
                                                            }
                                                        }
                                                    }
                                                }
                                            });
                                            return retCard;
                                        }),
                                    };
                                }
                            }) || [],
                        parentCategory: catObjectKey,
                    };

                    if (list.hasOwnProperty(catObjectKey)) {
                        list[catObjectKey].subCategories = {
                            ...list[catObjectKey].subCategories,
                            ...subCategoryList,
                        };
                        list[catObjectKey].list = Array.from(
                            new Set(list[catObjectKey].list.concat(subCategoryList[category].list))
                        );

                        const foundIds = [];
                        list[catObjectKey].list = list[catObjectKey].list.filter((item) => {
                            if (!foundIds.includes(item.id)) {
                                foundIds.push(item.id);
                                return true;
                            }

                            return false;
                        });

                        list[catObjectKey].count = list[catObjectKey].list.length;
                    } else {
                        items = items.concat(subCategoryList[category].list);
                    }
                } else {
                    items = deck.details.brackets.categories[category].list.map((item) => {
                        if (!cards?.hasOwnProperty(item)) {
                            let source = null;
                            deck?.details?.combos?.list?.forEach((combo) => {
                                if (combo.id === item) {
                                    source = combo;
                                }
                            });

                            if (source) {
                                return {
                                    id: item,
                                    type: 'combo',
                                    cards: (source?.cards || []).map((__card) => {
                                        if (cards.hasOwnProperty(__card)) {
                                            return cards?.[__card];
                                        }

                                        let retCard = null;
                                        Object.keys(cards || {}).forEach((cardId) => {
                                            if (cards[cardId].containerId === __card) {
                                                if (cards[cardId].isFrontFace) {
                                                    retCard = cards[cardId];
                                                } else {
                                                    if (!retCard) {
                                                        if (new RegExp(`_${__card}`).test(cardId)) {
                                                            retCard = cards[cardId];
                                                        } else {
                                                            if (new RegExp(`${__card}_back`).test(cardId)) {
                                                                retCard = cards[cardId];
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        });
                                        return retCard;
                                    }),
                                };
                            }
                        }

                        return {
                            ...item,
                            ...cards?.[item],
                        };
                    });
                }

                if (!list.hasOwnProperty(catObjectKey)) {
                    list[catObjectKey] = {
                        label: `${label}`,
                        id: catObjectKey,
                        count: items?.length || 0,
                        list: items,
                        color: `#FFFFFF`,
                        subCategories: subCategoryList,
                    };
                }
            });

        Object.keys(list).forEach((key) => {
            if (list?.[key]?.list?.length < 1) {
                delete list?.[key];
            } else {
                Object.keys(list?.[key]?.subCategories).forEach((subKey) => {
                    if (list?.[key]?.subCategories?.[subKey]?.list?.length < 1) {
                        delete list?.[key]?.subCategories?.[subKey];
                    }
                });
            }
        });

        return list;
    };

    const brackets = deck?.details?.brackets || {};
    const wotcBracket = brackets.wotcBracket;
    const csBracket = brackets.csBracket;
    const bracketCategories = brackets.categories || {};
    const reasons = brackets.reasons || {};

    const spike = deck?.details?.powerLevel?.ratings?.spike || {};

    const bias = deck?.details?.powerLevel?.biasExplanation || {};
    const compression = bias.intermediateValues?.compressionDetails || {};
    const detectedPatterns = compression.detectedPatterns || {};

    const bracketDelta = csBracket - wotcBracket;
    const deltaVerdict =
        bracketDelta < 0
            ? 'Softer than the criteria suggest — deck underperforms its WotC bracket.'
            : bracketDelta > 0
            ? 'Hits above the criteria suggest — deck outperforms its WotC bracket.'
            : 'Matches the criteria on both strict and practical reads.';

    const wotcCategoryKeys = ['gameChangers', 'massLandDenial', 'extraTurns', 'earlyGameInfiniteCombos'];

    const metrics = compression.metrics || {};
    const spikeScorePieces = deck?.details?.powerLevel?.spikeScorePieces || {};
    const interactionBits = spikeScorePieces.interaction || {};
    const efficiencyBits = spikeScorePieces.efficiency || {};
    const consistencyBits = spikeScorePieces.consistency || {};
    const winconBits = spikeScorePieces.winConditions || {};
    const manabasePct = deck?.details?.manabase?.percentages || {};
    const gcCount = bracketCategories.gameChangers?.count || 0;
    const mldCount = bracketCategories.massLandDenial?.count || 0;
    const etCount = bracketCategories.extraTurns?.count || 0;
    const egcCount = bracketCategories.earlyGameInfiniteCombos?.count || 0;

    const powerLevel = Number(deck?.powerLevelRating) || 0;
    const rationale = [];

    if (powerLevel < 5) {
        rationale.push({
            tone: 'down',
            text: `Power-level rating of ${powerLevel.toFixed(
                1
            )} puts the deck in bracket 2–3 territory regardless of which WotC categories it triggers.`,
        });
    } else if (powerLevel < 7) {
        rationale.push({
            tone: 'down',
            text: `Power-level rating of ${powerLevel.toFixed(
                1
            )} reads as an upgraded-casual build at the table, not an optimized one.`,
        });
    } else if (powerLevel < 9) {
        rationale.push({
            tone: 'up',
            text: `Power-level rating of ${powerLevel.toFixed(
                1
            )} puts the deck in optimized (bracket 4) territory on practical play.`,
        });
    } else {
        rationale.push({
            tone: 'up',
            text: `Power-level rating of ${powerLevel.toFixed(
                1
            )} indicates a competitive / cEDH-grade build.`,
        });
    }

    if (detectedPatterns.isBudgetControlShell) {
        rationale.push({
            tone: 'down',
            text: 'Budget control shell detected — most polish pieces are stand-ins, so the deck plays slower than the criteria imply.',
        });
    }
    if (detectedPatterns.isGoodstuffValueDeck) {
        rationale.push({
            tone: 'down',
            text: 'Reads as a goodstuff / value pile — lacks a cohesive closer, so flagged cards don\'t combine into a real threat.',
        });
    }
    if (detectedPatterns.isFocusedCasual && bracketDelta < 0) {
        rationale.push({
            tone: 'down',
            text: 'Focused-casual build pattern — themed tightly, but not weaponized at bracket-4 speed.',
        });
    }
    if (detectedPatterns.hasUnreliableWincons) {
        rationale.push({
            tone: 'down',
            text: 'Wincons read as unreliable — even with flagged enablers, closing the game isn\'t a given.',
        });
    }
    if (
        detectedPatterns.isStaxWithoutCombo &&
        !detectedPatterns.hasStrongComboBackbone
    ) {
        rationale.push({
            tone: 'down',
            text: 'Stax pieces present, but no combo line — locks slow games down without cleanly ending them.',
        });
    }
    if (
        detectedPatterns.isPureControlDeck &&
        (metrics.winconComponent || 0) < 0.3
    ) {
        rationale.push({
            tone: 'down',
            text: 'Pure-control shell with a thin wincon — deck answers threats all day but struggles to close.',
        });
    }
    if (detectedPatterns.isEnablerPileDeck) {
        rationale.push({
            tone: 'down',
            text: 'Enabler density is high but payoffs are thin — flagged pieces don\'t resolve into consistent wins.',
        });
    }
    if (detectedPatterns.isSlowCompetitiveDeck) {
        rationale.push({
            tone: 'down',
            text: 'Slow-competitive build — theoretically optimized but missing the opening-turn acceleration of a real bracket-4 deck.',
        });
    }
    if (detectedPatterns.lacksCompetitiveCore && wotcBracket >= 4) {
        rationale.push({
            tone: 'down',
            text: 'Lacks a competitive core — no Ad Naus / Necropotence / dedicated fast wincons to back the bracket-4 criteria.',
        });
    }

    if (detectedPatterns.hasStrongComboBackbone) {
        rationale.push({
            tone: 'up',
            text: 'Strong combo backbone detected — deck closes games faster than category-count alone would suggest.',
        });
    }
    if (detectedPatterns.isRedundantComboDeck) {
        rationale.push({
            tone: 'up',
            text: 'Redundant combo lines make this deck resilient — reads harder than the card-count criteria show.',
        });
    }
    if (detectedPatterns.hasDenseEnablerComboCore) {
        rationale.push({
            tone: 'up',
            text: 'Dense enabler-combo core — every turn of setup compounds toward a win.',
        });
    }
    if (detectedPatterns.isCEDHStaxBuild) {
        rationale.push({
            tone: 'up',
            text: 'cEDH-style stax density — lock pieces are tuned for tournament play, not casual noise.',
        });
    }
    if (detectedPatterns.hasEliteCompetitiveCoreForCompression) {
        rationale.push({
            tone: 'up',
            text: 'Elite competitive core detected — the mana-denial / protection suite is tournament-grade.',
        });
    }
    if (detectedPatterns.isIntentionalComboBuild) {
        rationale.push({
            tone: 'up',
            text: 'Intentional combo build — combo lines are baked into the deck\'s identity, not accidental inclusions.',
        });
    }

    if (manabasePct.quality != null && manabasePct.quality < 70) {
        rationale.push({
            tone: 'down',
            text: `Manabase quality is ${Math.round(
                manabasePct.quality
            )}% — color screw and tap-ins cost tempo at higher-power tables.`,
        });
    } else if (manabasePct.quality != null && manabasePct.quality >= 100) {
        rationale.push({
            tone: 'up',
            text: `Manabase quality is ${Math.round(
                manabasePct.quality
            )}% — on-color sources and fetch/dual density match higher-bracket expectations.`,
        });
    }

    if (spike.interaction != null && spike.interaction < 0.35) {
        rationale.push({
            tone: 'down',
            text: `Interaction density is ${Math.round(
                spike.interaction * 100
            )}% of the cEDH baseline — not enough answers to hold threats off at bracket-4 speed.`,
        });
    } else if (spike.interaction != null && spike.interaction >= 0.75) {
        rationale.push({
            tone: 'up',
            text: `Interaction density is ${Math.round(
                spike.interaction * 100
            )}% of the cEDH baseline — the reactive suite is genuinely threatening.`,
        });
    }

    if (spike.winConditions != null && spike.winConditions < 0.3) {
        rationale.push({
            tone: 'down',
            text: `Win-condition density is ${Math.round(
                spike.winConditions * 100
            )}% of the cEDH baseline — the path to victory is thin.`,
        });
    } else if (spike.winConditions != null && spike.winConditions >= 0.7) {
        rationale.push({
            tone: 'up',
            text: `Win-condition density is ${Math.round(
                spike.winConditions * 100
            )}% of the cEDH baseline — this deck has real closers.`,
        });
    }

    if (spike.efficiency != null && spike.efficiency < 0.35) {
        rationale.push({
            tone: 'down',
            text: `Efficiency is ${Math.round(
                spike.efficiency * 100
            )}% of baseline — deck is on a turn-6 clock, not a turn-3 one.`,
        });
    } else if (spike.efficiency != null && spike.efficiency >= 0.7) {
        rationale.push({
            tone: 'up',
            text: `Efficiency is ${Math.round(
                spike.efficiency * 100
            )}% of baseline — fast mana + ramp density is tournament-comparable.`,
        });
    }

    if (spike.consistency != null && spike.consistency < 0.3) {
        rationale.push({
            tone: 'down',
            text: `Consistency is ${Math.round(
                spike.consistency * 100
            )}% of baseline — few tutors or draw engines to reliably find the plan.`,
        });
    } else if (spike.consistency != null && spike.consistency >= 0.7) {
        rationale.push({
            tone: 'up',
            text: `Consistency is ${Math.round(
                spike.consistency * 100
            )}% of baseline — deck finds its pieces as reliably as a tuned build.`,
        });
    }

    const syn = deck?.synergyRating || 0;
    if (syn >= 1500) {
        rationale.push({
            tone: 'up',
            text: `Synergy rating of ${Math.round(
                syn
            )} indicates compounding card interactions typical of a tuned deck.`,
        });
    } else if (syn > 0 && syn < 400) {
        rationale.push({
            tone: 'down',
            text: `Synergy rating of ${Math.round(
                syn
            )} is thin — cards don't compound into explosive turns.`,
        });
    }

    const seenTexts = new Set();
    const finalRationale = rationale.filter((r) => {
        if (seenTexts.has(r.text)) return false;
        seenTexts.add(r.text);
        return true;
    });

    if (finalRationale.length === 0) {
        finalRationale.push({
            tone: 'flat',
            text: `Power level and shape-detection both land on bracket ${csBracket} — no major softening or hardening adjustments applied.`,
        });
    }

    const downgradeSuggestions = [];
    const upgradeSuggestions = [];
    const ruleZeroNotes = [];

    if (reasons.gameChangers) {
        if (gcCount >= 4) {
            const cutCount = gcCount - 3;
            downgradeSuggestions.push(
                `Cut ${cutCount} game-changer${cutCount === 1 ? '' : 's'} to drop under the 4+ threshold that flags bracket 4.`
            );
        } else if (gcCount > 0) {
            downgradeSuggestions.push(
                `Remove the remaining ${gcCount} game-changer${gcCount === 1 ? '' : 's'} to exit bracket-3 territory.`
            );
        }
    }
    if (reasons.massLandDenial) {
        downgradeSuggestions.push(
            `Cut the ${mldCount} mass land-denial effect${mldCount === 1 ? '' : 's'} — a single inclusion alone flags bracket 4.`
        );
    }
    if (reasons.extraTurns) {
        if (etCount >= 5) {
            downgradeSuggestions.push(
                `Trim ${etCount - 4} extra-turn spell${etCount - 4 === 1 ? '' : 's'} to drop under the 5+ threshold.`
            );
        } else if (etCount > 0) {
            downgradeSuggestions.push(
                `Remove extra-turn spells entirely to exit the bracket-3 push.`
            );
        }
    }
    if (reasons.earlyGameInfiniteCombos) {
        downgradeSuggestions.push(
            `Break up ${egcCount} early-game two-card combo${egcCount === 1 ? '' : 's'} — any inclusion reads as bracket 4.`
        );
    }
    if (detectedPatterns.isRedundantComboDeck) {
        downgradeSuggestions.push(
            'Redundant combo lines detected — commit to a single line and remove the backups to soften the bracket read.'
        );
    }
    if (detectedPatterns.hasDenseEnablerComboCore) {
        downgradeSuggestions.push(
            'The enabler-combo core is dense — thin the enablers if the goal is a casual shell.'
        );
    }
    if (detectedPatterns.isCEDHStaxBuild || detectedPatterns.isCEDHStaxForPenalty) {
        downgradeSuggestions.push(
            'cEDH-style stax density detected — loosen the lock pieces if you intend to land below bracket 4.'
        );
    }

    if ((manabasePct.quality ?? 100) < 85) {
        upgradeSuggestions.push(
            `Manabase quality is ${Math.round(manabasePct.quality)}% — better duals, fetchlands, and utility lands close the gap.`
        );
    }
    if ((manabasePct.curve ?? 100) < 95) {
        upgradeSuggestions.push(
            `Curve playability is ${Math.round(manabasePct.curve)}% — trim the top end or add ramp so hands cast on time.`
        );
    }
    if ((manabasePct.manaFixing ?? 100) < 85) {
        upgradeSuggestions.push(
            `Mana fixing is ${Math.round(manabasePct.manaFixing)}% — add more duals or fixing rocks to stabilize colors.`
        );
    }

    const interactionTotal = interactionBits.answers || 0;
    if (interactionTotal < 150) {
        upgradeSuggestions.push(
            `Interaction score is only ${Math.round(interactionTotal)} — add removal, counterspells, or stax to protect the gameplan.`
        );
    } else if ((spike.interaction ?? 0) < 0.3) {
        upgradeSuggestions.push(
            'Interaction density is under 30% of the cEDH baseline — swap dead slots for reactive spells.'
        );
    }
    if ((interactionBits.evasion || 0) < 10 && (winconBits.total || 0) > 120) {
        upgradeSuggestions.push(
            'Wincons present but no evasion — add protection or unblockable effects so the finisher actually lands.'
        );
    }

    if ((efficiencyBits.fastmana || 0) < 10 && wotcBracket >= 3) {
        upgradeSuggestions.push(
            'Fast mana is minimal — Sol Ring, signets, and 1-drop accelerants tighten the opening-turn game significantly.'
        );
    }
    if ((efficiencyBits.ramp || 0) < 40) {
        upgradeSuggestions.push(
            `Ramp density is ${Math.round(efficiencyBits.ramp || 0)} — add early green ramp or rocks to hit payoffs on time.`
        );
    }
    if ((efficiencyBits.avgcmc || 0) > 130) {
        upgradeSuggestions.push(
            'Average CMC is on the heavy side — trimming high-cost slots or adding cost-reduction speeds the deck up.'
        );
    }

    const winconTotal = winconBits.total || 0;
    if (winconTotal < 100) {
        upgradeSuggestions.push(
            `Win-condition score is only ${Math.round(winconTotal)} — add dedicated finishers or tighten the combo lines.`
        );
    }
    if (detectedPatterns.hasUnreliableWincons) {
        upgradeSuggestions.push(
            'Wincons read as unreliable — add redundancy, protection, or a secondary win path.'
        );
    }
    if (!metrics.hasMultiWinconDensity && winconTotal > 0 && (deck?.comboRating ?? 0) < 200) {
        upgradeSuggestions.push(
            'Only one viable win path detected — add a backup plan for when the primary line is disrupted.'
        );
    }

    if ((consistencyBits.draw || 0) < 40) {
        upgradeSuggestions.push(
            `Card draw is thin (${Math.round(consistencyBits.draw || 0)}) — ongoing draw engines smooth the late game.`
        );
    }
    if ((consistencyBits.recursion || 0) < 5 && detectedPatterns.isIntentionalComboBuild) {
        upgradeSuggestions.push(
            'Recursion is minimal — add ways to re-buy combo pieces after they get answered.'
        );
    }
    if ((metrics.tutorComponent || 0) < 0.3 && wotcBracket >= 3) {
        upgradeSuggestions.push(
            'Tutor density is low — tutors tighten consistency significantly for combo-leaning builds.'
        );
    }
    if ((deck?.synergyRating ?? 0) < 500) {
        upgradeSuggestions.push(
            `Synergy score is ${Math.round(deck?.synergyRating ?? 0)} — lean harder into the deck's theme for compounding value.`
        );
    }

    if (detectedPatterns.isGoodstuffValueDeck) {
        upgradeSuggestions.push(
            'This reads as a goodstuff pile — pick a focused theme (combo, aggro, stax) and commit to it.'
        );
    }
    if (detectedPatterns.isStaxWithoutCombo) {
        upgradeSuggestions.push(
            'Stax without a closer tends to drag games — add a combo or dedicated finisher so you actually end the table.'
        );
    }
    if (detectedPatterns.isPureControlDeck && winconTotal < 150) {
        upgradeSuggestions.push(
            'Pure-control shell with a thin wincon — a compact finisher keeps the control chain from stalling forever.'
        );
    }
    if (detectedPatterns.isEnablerPileDeck) {
        upgradeSuggestions.push(
            'Enabler density is high but payoffs are thin — add cards that actually win the game on their own.'
        );
    }
    if (detectedPatterns.isBudgetControlShell) {
        upgradeSuggestions.push(
            'Budget control shell detected — upgrading the manabase and a few top-tier counterspells gives the biggest jump.'
        );
    }
    if (detectedPatterns.isJankyCombo) {
        upgradeSuggestions.push(
            'Combo line reads as janky — tutors, protection, or a redundant line make it reliable.'
        );
    }
    if (detectedPatterns.lacksCompetitiveCore && wotcBracket >= 4) {
        upgradeSuggestions.push(
            'Deck lacks a competitive core for the WotC bracket it lands in — add Ad Naus / Necropotence / fast wincons if competing at that level.'
        );
    }
    if (detectedPatterns.isSlowCompetitiveDeck) {
        upgradeSuggestions.push(
            'Deck looks competitive but slow — audit the opening 3 turns and prune any card that doesn\'t advance the plan on curve.'
        );
    }

    if (bracketDelta > 0) {
        if (detectedPatterns.hasStrongComboBackbone) {
            ruleZeroNotes.push(
                'Strong combo backbone detected — expect this deck to end games faster than the WotC bracket suggests.'
            );
        }
        if (detectedPatterns.isRedundantComboDeck) {
            ruleZeroNotes.push(
                'Redundant combo lines make this deck more resilient than the WotC criteria imply.'
            );
        }
        if ((metrics.synergyComponent || 0) > 0.7) {
            ruleZeroNotes.push(
                'Dense synergy — plays chain together faster than the cEDH-staple count alone would indicate.'
            );
        }
        if ((metrics.tutorComponent || 0) > 0.7) {
            ruleZeroNotes.push(
                'High tutor density — the deck finds its key pieces far more reliably than a typical bracket-' +
                    wotcBracket +
                    ' build.'
            );
        }
        if (detectedPatterns.hasEliteCompetitiveCoreForCompression) {
            ruleZeroNotes.push(
                'Elite competitive core detected — players at the pod should expect tournament-level interaction.'
            );
        }
    }

    const uniqByText = (arr) => Array.from(new Set(arr));
    const finalDowngrade = uniqByText(downgradeSuggestions);
    const finalUpgrade = uniqByText(upgradeSuggestions);
    const finalNotes = uniqByText(ruleZeroNotes);

    return (
        <BaseDeckPane
            title={'Bracket Assessment'}
            fieldName="name"
            content={<div />}
            showScore={false}
            categories={getCategories()}
        >
            <Flex direction="column" gap="size-200">
                <section className="bracketVerdict">
                    <BracketBadge
                        number={wotcBracket}
                        label={BRACKET_LABELS[wotcBracket] || '—'}
                        tone="wotc"
                        subline="WotC baseline"
                    />
                    <div className="bracketVerdictDelta">
                        <span className="bracketVerdictDeltaArrow" aria-hidden="true">
                            {bracketDelta < 0 ? '↘' : bracketDelta > 0 ? '↗' : '→'}
                        </span>
                        <span className="bracketVerdictDeltaLabel">
                            {bracketDelta === 0 ? 'in sync' : `${bracketDelta > 0 ? '+' : ''}${bracketDelta}`}
                        </span>
                    </div>
                    <BracketBadge
                        number={csBracket}
                        label={BRACKET_LABELS[csBracket] || '—'}
                        tone="cs"
                        subline="Commandersalt"
                    />
                    <p className="bracketVerdictCopy">
                        {deltaVerdict}
                        <ContextualHelp variant="help">
                            <Heading>WotC vs. Commandersalt</Heading>
                            <Content>
                                <Text>
                                    The WotC bracket applies the strict criteria listed in the Brackets
                                    announcement (game changers, MLD, extra turns, and early-game infinite
                                    combos). The Commandersalt bracket softens that read using deck
                                    realities — manabase quality, synergy density, win-con reliability, and
                                    compression patterns — to estimate how the deck actually plays at table.
                                </Text>
                            </Content>
                        </ContextualHelp>
                    </p>
                </section>

                <section className="bracketSection">
                    <header className="bracketSectionHeader">
                        <span className="bracketSectionEyebrowRow">
                            <span className="bracketSectionScopeTag bracketSectionScopeTag--wotc">
                                WotC
                            </span>
                            <span className="bracketSectionEyebrow">Criteria checklist</span>
                        </span>
                        <span className="bracketSectionHint">
                            Each checks a specific Wizards-defined category. These drive the WotC bracket
                            directly — they do not affect the Commandersalt rating.
                        </span>
                    </header>
                    <div className="wotcCriteriaList">
                        {wotcCategoryKeys.map((k) => (
                            <WotcCriteriaRow
                                key={k}
                                categoryKey={k}
                                count={bracketCategories[k]?.count ?? 0}
                                isReason={!!reasons[k]}
                            />
                        ))}
                        {wotcBracket === 5 && (
                            <CompetitiveIntentRow
                                intent={computeCompetitiveIntent(
                                    wotcBracket,
                                    deck?.powerLevelRating
                                )}
                            />
                        )}
                    </div>
                </section>

                <section className="bracketSection">
                    <header className="bracketSectionHeader">
                        <span className="bracketSectionEyebrowRow">
                            <span className="bracketSectionScopeTag bracketSectionScopeTag--cs">
                                Commandersalt
                            </span>
                            <span className="bracketSectionEyebrow">Rating rationale</span>
                            <ContextualHelp variant="help">
                                <Heading>How the Commandersalt bracket is derived</Heading>
                                <Content>
                                    <Text>
                                        The Commandersalt bracket starts from the deck's overall
                                        power-level rating, then adjusts up or down based on shape
                                        signals the WotC criteria don't capture.
                                        <p />
                                        <b>Pillar scores are independent.</b> Consistency,
                                        Interaction, Efficiency, Win Conditions, and Manabase each
                                        measure a different thing. A strong manabase doesn't pull
                                        efficiency up — efficiency is about ramp, fast mana, and
                                        cost reduction. Manabase is about fixing, curve
                                        playability, and land quality.
                                        <p />
                                        <b>Softening factors (bracket pulled down):</b> budget
                                        control shells, goodstuff/value piles, unreliable wincons,
                                        stax without a closer, low interaction density, weak
                                        manabase, thin synergy.
                                        <p />
                                        <b>Hardening factors (bracket pushed up):</b> strong combo
                                        backbone, redundant combo lines, dense enabler core, elite
                                        competitive core, tournament-grade manabase, high
                                        interaction + wincon density.
                                        <p />
                                        The rationale below lists the factors that moved the read
                                        for this specific deck.
                                    </Text>
                                </Content>
                            </ContextualHelp>
                        </span>
                        <span className="bracketSectionHint">
                            {bracketDelta < 0
                                ? `Why the deck lands on bracket ${csBracket} despite the WotC criteria flagging bracket ${wotcBracket}.`
                                : bracketDelta > 0
                                ? `Why the deck plays at bracket ${csBracket} even though the strict WotC criteria only reach bracket ${wotcBracket}.`
                                : `Why both reads agree on bracket ${csBracket}.`}
                        </span>
                    </header>
                    <div className="bracketRationaleList">
                        {finalRationale.map((r, idx) => (
                            <div
                                className={`bracketRationaleRow bracketRationaleRow--${r.tone}`}
                                key={`rationale_${idx}`}
                            >
                                <span className="bracketRationaleArrow" aria-hidden="true">
                                    {r.tone === 'down' ? '↘' : r.tone === 'up' ? '↗' : '═'}
                                </span>
                                <span className="bracketRationaleText">{r.text}</span>
                            </div>
                        ))}
                    </div>
                </section>


                <section className="bracketSection">
                    <header className="bracketSectionHeader">
                        <span className="bracketSectionEyebrow">Verdict & Suggestions</span>
                        <span className="bracketSectionHint">
                            Specific moves to shift this deck toward or away from its current bracket —
                            generated from the deck's actual pillar scores, shape flags, and category counts.
                        </span>
                    </header>
                    <div className="bracketSuggestionsGrid">
                        {finalDowngrade.length > 0 && (
                            <div className="bracketSuggestionCard bracketSuggestionCard--down">
                                <span className="bracketSuggestionTitle">
                                    Soften the bracket read
                                    <span className="bracketSuggestionCount">
                                        {finalDowngrade.length}
                                    </span>
                                </span>
                                <p className="bracketSuggestionCopy">
                                    These are what's currently pushing this deck up toward bracket{' '}
                                    <b>{wotcBracket}</b>. Pull any of them to drift closer to bracket{' '}
                                    <b>{csBracket}</b> at the table.
                                </p>
                                <ul className="bracketSuggestionList">
                                    {finalDowngrade.map((s, i) => (
                                        <li key={`dg_${i}`}>{s}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {finalUpgrade.length > 0 && (
                            <div className="bracketSuggestionCard bracketSuggestionCard--up">
                                <span className="bracketSuggestionTitle">
                                    Tighten the build
                                    <span className="bracketSuggestionCount">
                                        {finalUpgrade.length}
                                    </span>
                                </span>
                                <p className="bracketSuggestionCopy">
                                    Specific weaknesses detected in this deck's shape. Addressing any of
                                    them raises the Commandersalt read without necessarily changing the
                                    WotC bracket.
                                </p>
                                <ul className="bracketSuggestionList">
                                    {finalUpgrade.map((s, i) => (
                                        <li key={`up_${i}`}>{s}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {finalNotes.length > 0 && (
                            <div className="bracketSuggestionCard bracketSuggestionCard--over">
                                <span className="bracketSuggestionTitle">
                                    Rule-zero notes
                                    <span className="bracketSuggestionCount">
                                        {finalNotes.length}
                                    </span>
                                </span>
                                <p className="bracketSuggestionCopy">
                                    This deck is likely to outperform its WotC bracket. Surface these
                                    before game one so the table isn't blindsided.
                                </p>
                                <ul className="bracketSuggestionList">
                                    {finalNotes.map((s, i) => (
                                        <li key={`note_${i}`}>{s}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {csBracket === wotcBracket &&
                            finalUpgrade.length === 0 &&
                            finalDowngrade.length === 0 && (
                                <div className="bracketSuggestionCard bracketSuggestionCard--match">
                                    <span className="bracketSuggestionTitle">Verdict</span>
                                    <p className="bracketSuggestionCopy">
                                        The system works. This reads as a solid bracket{' '}
                                        <b>{csBracket}</b> deck on both axes, with no material weaknesses
                                        or over-performing patterns detected.
                                    </p>
                                </div>
                            )}
                    </div>
                </section>
            </Flex>
        </BaseDeckPane>
    );
}
